/**
 * The clinic's stores.
 *
 * Read and open, not create. A store counts against the plan and gets its own
 * site and its own analytics line, so adding one changes what the clinic is
 * billed for - which is a JMC operation, from a screen that can see the
 * contract. Everything else about a store stays here: its name, its status, its
 * article defaults, and who works in it.
 *
 * A card each, not a row each. The list was a name, a status and a date - so
 * "which branch is this", "is anybody assigned to it" and "does it have anything
 * to write from" each cost a click, and a clinic with four branches had four
 * screens to visit to answer one question. Those three answers are on the card
 * now; the store screen behind it is where they are changed.
 */
import { useLoaderData, useNavigate } from 'react-router';
import { type ListResponse, type Store } from '../api.ts';
import { Card, Empty, StatusPill, jaDate, jaNumber } from '../components/ui.tsx';
import { AnimatePresence, listItem, m } from '../components/motion.tsx';
import { SkeletonPeople, usePendingScreen } from '../components/Skeleton.tsx';

const count = (value: string | number | null | undefined): number => Number(value ?? 0);

/** Where the branch is, in the shortest form that still identifies it. */
function whereabouts(store: Store): string {
  const area = [store.prefecture, store.city].filter(Boolean).join('');
  return area || store.address || '';
}

function StoreCard({ store, index, onOpen }: { store: Store; index: number; onOpen: () => void }) {
  const area = whereabouts(store);
  /*
   * The three numbers that say whether this store can produce an article at
   * all: who can act on it, what it may state, and what it has produced. A
   * store with staff and no approved material is stuck, and the card is where
   * that should be visible.
   */
  const figures: Array<[string, number]> = [
    ['担当ユーザー', count(store.member_count)],
    ['承認済みの情報', count(store.fact_count)],
    ['記事', count(store.article_count)],
  ];

  return (
    <m.button
      className="storecard"
      type="button"
      data-motion
      layout
      {...listItem(index)}
      onClick={onOpen}
    >
      <span className="storecard__head">
        <span className="storecard__name">{store.name}</span>
        <StatusPill status={store.status} />
      </span>

      <span className="storecard__where">
        {area !== '' ? area : <span className="muted">所在地が未登録です</span>}
      </span>

      {store.hours_note && <span className="storecard__hours">{store.hours_note}</span>}

      <span className="storecard__figures">
        {figures.map(([label, value]) => (
          <span key={label} className="storecard__figure">
            <span className="storecard__value">{jaNumber(value)}</span>
            <span className="storecard__label">{label}</span>
          </span>
        ))}
      </span>

      <span className="storecard__foot muted small">登録日 {jaDate(store.created_at)}</span>
    </m.button>
  );
}

export default function Stores() {
  const { items } = useLoaderData<ListResponse<Store>>();
  const navigate = useNavigate();
  const pending = usePendingScreen();

  return (
    <Card
      title="クリニック・店舗管理"
      caption="店舗を選ぶと、その店舗の設定・所在地・担当ユーザーを確認して編集できます。店舗の追加はJMCへご依頼ください。"
    >
      {pending ? (
        <SkeletonPeople count={3} />
      ) : items.length === 0 ? (
        <Empty>
          店舗がまだ登録されていません。店舗の登録はJMCが行いますので、担当者にご連絡ください。
        </Empty>
      ) : (
        <div className="pgrid pgrid--wide">
          <AnimatePresence mode="popLayout">
            {items.map((store, index) => (
              <StoreCard
                key={store.id}
                store={store}
                index={index}
                onOpen={() => void navigate(`/stores/${store.id}`)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </Card>
  );
}
